/** Layout principal de la app: Sider siempre colapsado (o drawer en mobile) con menú según el módulo activo, header, redirección al primer ítem válido y SettingsDrawer lazy. */
import React, { useEffect, useRef, useState, lazy, Suspense } from "react";
import { Layout, Menu, Drawer } from "antd";
import { useNavigate } from "react-router-dom";
import { MdClose } from "react-icons/md";
import "./index.css";
import LayoutHeader from "./header/LayoutHeader";
import LayoutBody from "./body/LayoutBody";
import { useUIStore } from "@/stores/uiStore";
import { getModuleItems } from "@/utils";
import { iconMap, secondaryItems } from "./menuConfig";
import Spinner from "@/components/spinner/Spinner";

const SettingsDrawer = lazy(() => import("../settingsDrawer/SettingsDrawer"));

const { Sider } = Layout;

interface MainLayoutProps {
  children?: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const currentModule = useUIStore((s) => s.currentModule);
  const itemsMenu = useUIStore((s) => s.itemsMenu);
  const setCurrentMenuItems = useUIStore((s) => s.setCurrentMenuItems);

  const [isMobile, setIsMobile] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [sideBarMenuKey, setSideBarMenuKey] = useState<string>("");
  const [settingsDrawerOpen, setSettingsDrawerOpen] = useState(false);

  const previousModuleRef = useRef<number | undefined>(undefined);

  const currentModuleItems = getModuleItems(itemsMenu, currentModule);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 780;
      setIsMobile(mobile);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (currentModuleItems.length > 0) {
      setCurrentMenuItems(currentModuleItems);

      const currentPath = window.location.pathname.replace("/", "");
      const isPathValid = currentModuleItems.some((item) => item.pathProceso === currentPath);

      if (isPathValid) {
        const matched = currentModuleItems.find((item) => item.pathProceso === currentPath);
        if (matched) setSideBarMenuKey(`/${matched.pathProceso}`);
      } else if (previousModuleRef.current !== currentModule) {
        navigate(`/${currentModuleItems[0]?.pathProceso || "home"}`);
      }
    }

    previousModuleRef.current = currentModule;
  }, [currentModule, itemsMenu, navigate]);

  const handleMenuSelect = (info: { key: string }) => {
    const selectedItem = currentModuleItems.find((item) => `/${item.pathProceso}` === info.key);
    if (selectedItem) {
      setSideBarMenuKey(info.key);
      setMobileDrawerOpen(false);
      navigate(`/${selectedItem.pathProceso}`);
    }
  };

  const handleSecondaryMenuClick = (info: { key: string }) => {
    if (info.key === "9") {
      // Deshabilitado por ahora: "Próximamente".
      return;
    }
  };

  const sidebarContent = (isDrawer = false) => (
    <div className={`sidebar-content ${isDrawer ? "sidebar-content--drawer" : ""}`}>
      {isDrawer && (
        <div className="sidebar-drawer-header">
          <img src="/innercor-blanco.svg" alt="Innercor" className="sidebar-drawer-logo" />
          <MdClose
            size={20}
            className="sidebar-drawer-close"
            onClick={() => setMobileDrawerOpen(false)}
          />
        </div>
      )}
      {!isDrawer && (
        <div className="sider-image-wrapper" onClick={() => navigate("/home")}>
          <img
            src="/logo-blanco-small.svg"
            alt="Innercor"
            // className="sider-logo-small"
            width={50}
          />
        </div>
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          flexDirection: "column",
          height: isDrawer ? "calc(100% - 80px)" : "90%",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Menu
          style={{
            marginBottom: "20px",
            flex: "1 1 auto",
            minHeight: 0,
            overflowY: "auto",
          }}
          items={currentModuleItems.map((item) => ({
            key: `/${item.pathProceso}`,
            icon: iconMap[item.iconoProceso] || undefined,
            label: item.nombreItemMenu,
          }))}
          theme="dark"
          selectedKeys={[sideBarMenuKey]}
          mode="inline"
          onSelect={handleMenuSelect}
        />
        <Menu
          items={secondaryItems}
          theme="dark"
          selectedKeys={[sideBarMenuKey]}
          mode="inline"
          onClick={handleSecondaryMenuClick}
        />
      </div>
    </div>
  );

  return (
    <>
      <Layout style={{ height: "100vh", overflow: "hidden" }}>
        {isMobile ? (
          <Drawer
            placement="left"
            open={mobileDrawerOpen}
            onClose={() => setMobileDrawerOpen(false)}
            width={260}
            closable={false}
            className="mobile-sidebar-drawer"
            styles={{
              body: { padding: 0, overflow: "hidden" },
              mask: { backgroundColor: "rgba(0, 0, 0, 0.45)" },
            }}
          >
            {sidebarContent(true)}
          </Drawer>
        ) : (
          <Sider trigger={null} collapsedWidth={80} collapsed>
            {sidebarContent()}
          </Sider>
        )}
        <Layout
          style={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <LayoutHeader
            menuItems={currentModuleItems}
            setSideBarMenuKey={setSideBarMenuKey}
            onOpenMobileSidebar={setMobileDrawerOpen}
            isMobile={isMobile}
          />
          <LayoutBody>{children}</LayoutBody>
        </Layout>
      </Layout>
      <Suspense fallback={<Spinner />}>
        <SettingsDrawer open={settingsDrawerOpen} onClose={() => setSettingsDrawerOpen(false)} />
      </Suspense>
    </>
  );
};

export default MainLayout;
